import {
  Joyride,
  STATUS,
  EVENTS,
  type EventData,
  type TooltipRenderProps,
} from "react-joyride";
import { Button } from "@/components/ui/button";
import { markTourSeen, TOURS_ENABLED } from "@/lib/tourState";
import type { Step } from "react-joyride";
import type { TourDef } from "@/lib/tourConfig";

interface AppTourProps {
  tour: TourDef | null;
  onClose: () => void;
  /** Override steps (e.g. dynamic property detail tour). */
  steps?: Step[];
  tourId?: string;
}

function TourTooltip({
  index,
  step,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  size,
}: TooltipRenderProps) {
  const isLast = index === size - 1;
  return (
    <div
      {...tooltipProps}
      className="bg-background border border-border rounded-2xl shadow-xl p-5 w-[min(340px,90vw)] font-sans z-[10001]"
    >
      <div className="flex gap-1.5 mb-3">
        {Array.from({ length: size }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? "w-4 bg-primary" : "w-1.5 bg-muted-foreground/30"
            }`}
          />
        ))}
      </div>

      {step.title && (
        <p className="font-semibold text-base text-foreground mb-1">
          {step.title as string}
        </p>
      )}
      <p className="text-sm text-muted-foreground leading-relaxed">
        {step.content as string}
      </p>

      <div className="flex items-center justify-between mt-4 gap-2">
        <button
          {...skipProps}
          type="button"
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Skip tour
        </button>
        <div className="flex gap-2">
          {index > 0 && (
            <Button
              {...backProps}
              variant="outline"
              size="sm"
              className="h-8 px-3 text-xs"
            >
              Back
            </Button>
          )}
          <Button {...primaryProps} size="sm" className="h-8 px-4 text-xs">
            {isLast ? "Done" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AppTour({ tour, onClose, steps: stepsOverride, tourId }: AppTourProps) {
  const resolvedSteps = stepsOverride ?? tour?.steps;
  const resolvedTourId = tourId ?? tour?.id;
  if (!TOURS_ENABLED || !resolvedSteps?.length || !resolvedTourId) return null;

  const handleEvent = (data: EventData) => {
    const { status, type } = data;
    if (type === EVENTS.TARGET_NOT_FOUND) {
      console.warn("[AppTour] Target not found for step", data.index, data.step);
    }
    if (
      status === STATUS.FINISHED ||
      status === STATUS.SKIPPED ||
      type === EVENTS.TOUR_END
    ) {
      markTourSeen(resolvedTourId);
      onClose();
    }
  };

  return (
    <Joyride
      steps={resolvedSteps}
      run
      continuous
      scrollToFirstStep
      scrollDuration={400}
      onEvent={handleEvent}
      tooltipComponent={TourTooltip}
      options={{
        buttons: ["back", "skip", "primary"],
        showProgress: false,
        skipBeacon: true,
        spotlightPadding: 6,
        scrollOffset: 80,
        targetWaitTimeout: 6000,
        beforeTimeout: 8000,
        overlayColor: "rgba(0, 0, 0, 0.45)",
        zIndex: 10000,
        arrowColor: "hsl(var(--background))",
      }}
      styles={{
        overlay: {
          backgroundColor: "rgba(0, 0, 0, 0.45)",
        },
        floater: {
          filter: "none",
        },
      }}
    />
  );
}
